/* eslint-disable react-hooks/exhaustive-deps */
/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { gql, useMutation } from "@apollo/client";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { displayAdapters, videoAdaptersConfig } from "../../constants";
import { pushMessage } from "../../reducers/messagingReducer";
import SectionHeader from "../helper/SectionHeader";
import LayoutWrapper from "../LayoutWrapper";
import { MULTI_FIELD_READ_MUTATION, MULTI_FIELD_UPDATE_MUTATION } from "./utils/constants";
import { InputText } from "primereact/inputtext";
import { Accordion, AccordionTab } from "primereact/accordion";
import { sspVideoParameterRow } from "./common/syndication/styles";
import DisplaySspParametersTable from "./common/syndication/components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckSquare, faXmarkSquare } from "@fortawesome/free-solid-svg-icons";
import { InputNumber } from "primereact/inputnumber";
import deepcopy from "deepcopy";

let infoStyles = css`
    display: inline-block;
    margin-right: 0.5em;
    color: rgb(132, 135, 139);
    margin-top: 0.75em;
    font-weight: 400;
    font-size: 12px;
`

/**
 * A tuple describing an adapter parameter.
 * [parameterName, parameterType, humanReadableLabel]
 *
 * @typedef {[string, string, string]} AdapterParameterTuple
 */

/**
 * Configuration for a display or video adapter.
 *
 * @typedef {Object} AdapterConfig
 * @property {AdapterParameterTuple[]} adapterParametersNames
 *   An array of parameter descriptors, where each descriptor is
 *   a 3-element tuple of strings:
 *   [name, type, label]
 * @property {string[]} [optionalParameters]
 *   List of parameter names that are optional.
 * @property {string} [adapterPrettyName]
 *   Optional human-friendly adapter name.
 */

/**
 * Entry for a bidder, containing display and video configurations.
 *
 * @typedef {Object} BidderEntry
 * @property {AdapterConfig} display
 * @property {AdapterConfig|null} video
 */

/**
 * Map of bidder names to their configurations.
 * The outermost object keys are dynamic strings.
 *
 * @typedef {Object.<string, BidderEntry>} BidderConfigMap
 */


const SyndicationPlacementId = () => {
    const { currentDomain, currentCustomer } = useSelector((state) => state.userInformation);
    const dispatch = useDispatch();

    /** @type {[BidderConfigMap]} */
    const [consolidatedAdaptersList, setConsolidatedAdaptersList] = useState([])
    const [syndicationAdPositions, setSyndicationAdPositions] = useState({})
    const [multiFieldReadMutation] = useMutation(MULTI_FIELD_READ_MUTATION)
    const [multiFieldUpdateMutation] = useMutation(MULTI_FIELD_UPDATE_MUTATION)
    const [sspState, setSspState] = useState({})


    async function retrieveDomainData() {
        try {
            let domainData = await multiFieldReadMutation({
                variables: {
                    fieldDefinitions: JSON.stringify([
                        { field_name: 'syndication_ad_positions', post_id: currentDomain, parse: true },
                        { field_name: 'adv_preset', post_id: currentDomain, parse: false },
                        { field_name: 'syndication_slot_placement_ids', post_id: currentDomain, parse: false }
                    ])
                }
            })
            let data = JSON.parse(domainData.data.multiFieldRetrieveMutation.response)
            let presetId = data.adv_preset.ID
            let presetData = await multiFieldReadMutation({
                variables: {
                    fieldDefinitions: JSON.stringify([
                        { field_name: 'global_video_adapter_selection', post_id: presetId, parse: false },
                        { field_name: 'global_prebid_adapter_selection', post_id: presetId, parse: false }

                    ])
                }
            })
            let presetAdaptersData = JSON.parse(presetData.data.multiFieldRetrieveMutation.response)
            /** @type {{global_video_adapter_selection: string[], global_prebid_adapter_selection: string[]}} */
            let {
                global_video_adapter_selection: presetVideoSspList,
                global_prebid_adapter_selection: presetDisplaySspList
            } = presetAdaptersData
            let displayAdapterSet = new Set(presetDisplaySspList?.map(item => item.toLowerCase()) || [])
            let videoAdapterSet = new Set(presetVideoSspList?.map(item => item.toLowerCase()) || [])

            const initial = Object.create(null)
            for (let [adapterName, adapterConfiguration] of Object.entries(displayAdapters)) {
                if (displayAdapterSet.has(adapterName)) {
                    initial[adapterName] = { display: adapterConfiguration, video: null }
                }
            }
            for (let [adapterName, adapterConfiguration] of Object.entries(videoAdaptersConfig)) {
                if (videoAdapterSet.has(adapterName)) {
                    if (initial[adapterName]) {
                        initial[adapterName].video = adapterConfiguration
                    } else {
                        initial[adapterName] = { video: adapterConfiguration, display: null }
                    }
                }
            }

            let syndicationSspConfiguration = {}
            try {
                syndicationSspConfiguration = JSON.parse(data.syndication_slot_placement_ids) || {}
            } catch {
                syndicationSspConfiguration = {}
            }

            setSspState(syndicationSspConfiguration)
            setSyndicationAdPositions(data.syndication_ad_positions || {})
            let sortedSsps = Object.entries(initial)
                .sort(([sspName], [sspName2]) => {
                    return sspName.localeCompare(sspName2)
                })
                .reduce((acc, [sspName, sspConfiguration]) => {
                    acc[sspName] = sspConfiguration
                    return acc
                }, Object.create(null))

            setConsolidatedAdaptersList(sortedSsps)

        } catch (err) {
            console.log(err)
        }
    }

    /**
     * 
     * @param {string} parameterName 
     * @param {"tab" | "mob" | "desk"} device 
     * @param {string} value 
     * @param {string} adPositionName 
     * @param {string} sspName 
     * @param {"banner" | "video"} format
     */
    function handleInputChange(parameterName, device, value, adPositionName, sspName, format) {
        let state = { ...sspState, [adPositionName]: sspState[adPositionName] || {} }
        let keys = [[sspName, {}], [format, {}], [device, {}], [0, {}]]

        if (format === 'video') {
            keys.splice(2, 1)
        }


        let lastObject = keys.reduce((acc, curr) => {
            let [key, defaultValue] = curr
            acc[key] = acc[key] || defaultValue
            return acc[key]
        }, state[adPositionName])

        lastObject[parameterName] = value
        setSspState(state)
    }

    useEffect(() => {
        if (currentCustomer && currentDomain) {
            retrieveDomainData()
        }
    }, [currentDomain, currentCustomer]);

    return (
        <LayoutWrapper model="domain" saveCallback={async () => {
            dispatch(pushMessage("SSP Configuration", "Saving SSPs configuration"));
            let sspConfiguration = deepcopy(sspState)

            function cleanObjectFromEmptyProperties(sspConfig) {
                function helper(parentObject, child, childKey) {
                    if (child !== null && typeof child === 'object') {
                        // Use Object.keys to avoid prototype pollution and
                        // ensure we are iterating over a stable list of keys
                        const keys = Object.keys(child);

                        for (const key of keys) {
                            helper(child, child[key], key);
                        }

                        // Check if object is empty without creating a new array of entries
                        if (Object.keys(child).length === 0) {
                            delete parentObject[childKey];
                        }
                    } else {
                        // Clean up null or empty strings
                        if (child === '' || child === null || child === undefined) {
                            delete parentObject[childKey];
                        }
                    }
                }

                // Use a dummy object to catch the deletion of the root if it becomes empty
                const rootWrapper = { root: sspConfig };
                helper(rootWrapper, sspConfig, 'root');
            }

            try {
                cleanObjectFromEmptyProperties(sspConfiguration)
            } catch {
                sspConfiguration = sspState
            }

            try {
                await multiFieldUpdateMutation({
                    variables: {
                        rules: JSON.stringify([
                            {
                                field_name: 'syndication_slot_placement_ids',
                                post_id: String(currentDomain),
                                value: JSON.stringify(sspConfiguration)
                            }
                        ])
                    }
                })
                dispatch(pushMessage('Syndication adapters', 'Configuration saved'))
            } catch (err) {
                dispatch(pushMessage('Syndication adapters', 'There was an error trying to save your changes', 'error'))
                console.log({ err })
            }
        }}>

            <div className={`p-panel p-component`}>
                <SectionHeader screenName="Display SSP Adapters: ">
                    <div className="p-d-flex p-ai-center f-width">
                        <p>
                            <b>Syndication Creative Unit Adapters</b>
                        </p>
                    </div>
                </SectionHeader>
                <div className="p-panel-content slot-placement" css={css`.p-accordion-header-text { text-transform: capitalize; }`}>
                    <Accordion multiple>
                        {
                            Object
                                .entries(consolidatedAdaptersList)
                                .map(([adapterName, adapterConfiguration], idx) => {
                                    let { display, video } = adapterConfiguration
                                    let formattedName = display?.adapterPrettyName
                                        || video?.adapterPrettyName
                                        || adapterName
                                        || "n/a"
                                    return (
                                        <AccordionTab
                                            key={adapterName}
                                            headerStyle={{ borderBottom: '1px solid #666' }}
                                            headerClassName="accordion-tab"
                                            header={
                                                <div>
                                                    <div
                                                        className="information"
                                                        css={css`
                                                            .icon {
                                                                display: inline-block;
                                                                margin-right: 0.5em; 
                                                            }
                                                        `}
                                                    >
                                                        {/* {formattedName.toUpperCase()} &nbsp; */}
                                                        <div className="formats">
                                                            {formattedName.toUpperCase()} &nbsp;
                                                            <div className="formats">
                                                                <span css={infoStyles} className="font-weight-700">
                                                                    SUPPORTED FORMATS: &nbsp;
                                                                </span>
                                                                <span css={infoStyles} className="icon">
                                                                    Display: <FontAwesomeIcon icon={display ? faCheckSquare : faXmarkSquare} color={display ? '#419d78' : '#f26419'} />
                                                                </span>
                                                                <span css={infoStyles} className="icon">
                                                                    Video: <FontAwesomeIcon icon={video ? faCheckSquare : faXmarkSquare} color={video ? '#419d78' : '#f26419'} />
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            }
                                        >
                                            <div>
                                                {
                                                    Object.keys(syndicationAdPositions).length
                                                        ? (
                                                            <Accordion multiple>
                                                                {

                                                                    Object
                                                                        .entries(syndicationAdPositions)
                                                                        .map(([adPositionName, data], positionIndex) => {
                                                                            let sspValues = sspState?.[adPositionName]?.[adapterName]?.banner || {}
                                                                            let {
                                                                                desk = { 0: {} },
                                                                                tab = { 0: {} },
                                                                                mob = { 0: {} }
                                                                            } = sspValues

                                                                            let desktopSizes = syndicationAdPositions[adPositionName]?.banner?.desk?.adsize || null
                                                                            let tabSizes = syndicationAdPositions[adPositionName]?.banner?.tab?.adsize || null
                                                                            let mobSizes = syndicationAdPositions[adPositionName]?.banner?.mob?.adsize || null
                                                                            let videoAdUnit = syndicationAdPositions[adPositionName]?.video?.network_id || 'NA'
                                                                            let isVideoAdapterEnabled = Array.isArray(video?.adapterParametersNames) && video.adapterParametersNames.length

                                                                            return (
                                                                                <AccordionTab
                                                                                    headerStyle={{
                                                                                        borderBottom: '1px solid #666'
                                                                                    }}
                                                                                    header={
                                                                                        <div>
                                                                                            <div>
                                                                                                {adPositionName.replaceAll('_', ' ')}
                                                                                            </div>
                                                                                            <div css={infoStyles} className="sizes-info">Desktop: [ {desktopSizes} ]</div>
                                                                                            <div css={infoStyles} className="sizes-info">Tablet: [ {tabSizes} ]</div>
                                                                                            <div css={infoStyles} className="sizes-info">Mobile: [ {mobSizes} ]</div>
                                                                                            <div>
                                                                                                <div css={infoStyles}><b>Configured video ad unit:</b> {videoAdUnit}</div>
                                                                                            </div>
                                                                                        </div>
                                                                                    }
                                                                                    key={positionIndex}
                                                                                >
                                                                                    <div css={css`display: flex; flex-direction: column;`} >
                                                                                        {
                                                                                            isVideoAdapterEnabled && (
                                                                                                <>
                                                                                                    <p><strong>Video Configuration</strong></p>
                                                                                                    {
                                                                                                        video
                                                                                                            ?.adapterParametersNames
                                                                                                            .map(([name, type, prettyName], parameterIndex) => {
                                                                                                                let sspValues = sspState?.[adPositionName]?.[adapterName]?.video || {}
                                                                                                                let { 0: rowValue } = sspValues
                                                                                                                let isNumberParam = type === 'number'
                                                                                                                let props = {
                                                                                                                    value: rowValue?.[name] || '',
                                                                                                                    onChange: ev => {
                                                                                                                        handleInputChange(
                                                                                                                            name,
                                                                                                                            null,
                                                                                                                            isNumberParam ? ev.value : ev.target.value,
                                                                                                                            adPositionName,
                                                                                                                            adapterName,
                                                                                                                            "video"
                                                                                                                        )
                                                                                                                    }
                                                                                                                }
                                                                                                                let Component = isNumberParam
                                                                                                                    ? InputNumber
                                                                                                                    : InputText

                                                                                                                if (isNumberParam) {
                                                                                                                    props.useGrouping = false
                                                                                                                }

                                                                                                                return (
                                                                                                                    <div
                                                                                                                        key={name}
                                                                                                                        className="video-ssp-row"
                                                                                                                        css={css`${sspVideoParameterRow}`}
                                                                                                                    >
                                                                                                                        <span className="ssp-name">{prettyName || name}</span>
                                                                                                                        <Component {...props} />
                                                                                                                    </div>
                                                                                                                )
                                                                                                            })
                                                                                                    }
                                                                                                    <hr />
                                                                                                </>
                                                                                            )
                                                                                        }
                                                                                        <p><strong>Display Configuration</strong></p>
                                                                                        {
                                                                                            display?.adapterParametersNames
                                                                                                ? (
                                                                                                    <DisplaySspParametersTable
                                                                                                        adapterName={adapterName}
                                                                                                        display={display}
                                                                                                        deskValues={desk}
                                                                                                        tabValues={tab}
                                                                                                        mobValues={mob}
                                                                                                        changeHandler={(paramName, device, value) => {
                                                                                                            handleInputChange(
                                                                                                                paramName,
                                                                                                                device,
                                                                                                                value,
                                                                                                                adPositionName,
                                                                                                                adapterName,
                                                                                                                "banner"
                                                                                                            )
                                                                                                        }}
                                                                                                    />
                                                                                                )
                                                                                                : null
                                                                                        }
                                                                                    </div>
                                                                                </AccordionTab>
                                                                            )
                                                                        })
                                                                }
                                                            </Accordion>
                                                        )
                                                        : (
                                                            <h6 style={{ color: '#ff0000' }}>Ad positions must first be configured under <i><b>Ad Positions &rang; Syndication Ads</b></i></h6>
                                                        )
                                                }
                                            </div>
                                        </AccordionTab>
                                    )
                                })
                        }
                    </Accordion>
                </div>
            </div>
        </LayoutWrapper>
    );
};

export default SyndicationPlacementId;
